// Feedback a tester sends from any screen: its kind, their words, and the
// screen, app version and platform, attached automatically. Nothing else is
// attached: no screenshot and no content from the user's notes or tasks.
// The database (public.feedback) enforces the same limits.

export type FeedbackKind = 'bug' | 'suggestion' | 'feedback';
export type FeedbackPlatform = 'web' | 'android' | 'ios';

export const FEEDBACK_KINDS: readonly FeedbackKind[] = ['bug', 'suggestion', 'feedback'];

export const FEEDBACK_KIND_LABELS: Record<FeedbackKind, string> = {
  bug: 'Bug',
  suggestion: 'Suggestion',
  feedback: 'Feedback',
};

/** The longest message, in characters as the database counts them. */
export const FEEDBACK_MESSAGE_MAX = 4000;
const SCREEN_MAX = 200;

/** The immutable content submitted through submit_feedback. The database derives user_id, status and created_at. */
export interface FeedbackRow {
  kind: FeedbackKind;
  message: string;
  screen: string;
  app_version: string;
  platform: FeedbackPlatform;
}

export type FeedbackValidation = { ok: true; row: FeedbackRow } | { ok: false; error: string };

/**
 * Where a piece of feedback stands (CER-66): new (nobody has acted on it
 * yet), ticketed (a ticket carries it), done, or wont_do. Only the service
 * role changes it; docs/FEEDBACK.md says how.
 */
export type FeedbackStatus = 'new' | 'ticketed' | 'done' | 'wont_do';

/** A piece of feedback as its sender reads it back: their own rows only (RLS). */
export interface FeedbackItem {
  id: string;
  kind: FeedbackKind;
  message: string;
  status: FeedbackStatus;
  /** The Linear ticket carrying it, such as CER-123. */
  ticket: string | null;
  /** Why it is done or will not be done. */
  resolution: string | null;
  created_at: string;
  updated_at: string;
}

/** The columns "Your feedback" reads. */
export const FEEDBACK_ITEM_COLUMNS = 'id, kind, message, status, ticket, resolution, created_at, updated_at';

/** What the sender sees: "Saved", "Ticketed · CER-123", "Done", or "Won't do: <reason>". */
export function feedbackStatusLabel(item: Pick<FeedbackItem, 'status' | 'ticket' | 'resolution'>): string {
  switch (item.status) {
    case 'new':
      return 'Saved';
    case 'ticketed':
      return item.ticket ? `Ticketed · ${item.ticket}` : 'Ticketed';
    case 'done':
      return item.resolution ? `Done: ${item.resolution}` : 'Done';
    case 'wont_do':
      return item.resolution ? `Won't do: ${item.resolution}` : "Won't do";
  }
}

const UUID_SEGMENT = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The screen a web address names, without the ids in it: "/projects/<uuid>"
 * becomes "/projects/:id". Query strings and fragments are dropped, so only
 * the route is attached.
 */
export function screenOfPath(pathname: string): string {
  const path = pathname.split(/[?#]/)[0];
  const segments = path
    .split('/')
    .filter(Boolean)
    .map(segment => (UUID_SEGMENT.test(segment) ? ':id' : segment));
  return `/${segments.join('/')}`.slice(0, SCREEN_MAX);
}

/** A message ready to save: surrounding spaces removed, one to FEEDBACK_MESSAGE_MAX characters. */
export function validateFeedback(input: {
  kind: FeedbackKind;
  message: string;
  screen: string;
  appVersion: string;
  platform: FeedbackPlatform;
}): FeedbackValidation {
  const message = input.message.trim();
  if (!message) return { ok: false, error: 'Write a few words first' };
  const length = [...message].length;
  if (length > FEEDBACK_MESSAGE_MAX) {
    return { ok: false, error: `Keep it under ${FEEDBACK_MESSAGE_MAX.toLocaleString('en-US')} characters (it is ${length.toLocaleString('en-US')})` };
  }
  if (!FEEDBACK_KINDS.includes(input.kind)) return { ok: false, error: 'Choose bug, suggestion or feedback' };
  if (!/^[0-9A-Za-z.+_-]{1,64}$/.test(input.appVersion)) return { ok: false, error: 'The app version is invalid' };
  if ([...input.screen].some(character => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) return { ok: false, error: 'The screen name is invalid' };
  if (!['web', 'android', 'ios'].includes(input.platform)) return { ok: false, error: 'The feedback platform is invalid' };
  return {
    ok: true,
    row: {
      kind: input.kind,
      message,
      screen: input.screen.slice(0, SCREEN_MAX) || 'unknown',
      app_version: input.appVersion,
      platform: input.platform,
    },
  };
}
